import React, { useState, useEffect } from 'react';
import Table, { Column } from 'ui/Table';
import StatCard from 'ui/StatCard';
import Loading from 'ui/Loading';
import Button from 'ui/Button';
import PageContainer from 'ui/PageContainer';
import PageHeader from 'ui/PageHeader';
import StatsGrid from 'ui/StatsGrid';
import FilterSection from 'ui/FilterSection';
import TableContainer from 'ui/TableContainer';
import SearchInput from 'ui/SearchInput';
import { PlusIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import './style.scss';

interface ApprovalDocument {
    id: number;
    document_type: 'expense' | 'purchase' | 'travel' | 'other';
    title: string;
    requester: string;
    requester_department: string;
    amount?: number;
    request_date: string;
    status: 'pending' | 'first_approved' | 'final_approved' | 'rejected' | 'returned';
    first_approver?: string;
    final_approver?: string;
    first_approval_date?: string;
    final_approval_date?: string;
    management_support_confirmed: boolean;
    description?: string;
    attachments?: string[];
    rejection_reason?: string;
    priority: 'high' | 'normal' | 'low';
}

interface ApprovalForm {
    document_type: string;
    title: string;
    amount: number;
    description: string;
    priority: string;
}

const ApprovalManagementPage: React.FC = () => {
    const [documents, setDocuments] = useState<ApprovalDocument[]>([]);
    const [filteredDocuments, setFilteredDocuments] = useState<ApprovalDocument[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedType, setSelectedType] = useState('all');
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [selectedPriority, setSelectedPriority] = useState('all');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState<ApprovalDocument | null>(null);
    const [loading, setLoading] = useState(true);
    const [approvalForm, setApprovalForm] = useState<ApprovalForm>({
        document_type: 'expense',
        title: '',
        amount: 0,
        description: '',
        priority: 'normal'
    });

    // 현재 사용자 역할 (실제로는 context나 props에서 가져와야 함)
    const [userRole] = useState<'admin' | 'user'>('admin'); // 'admin': 경영지원이사, 'user': 책임감리원

    // Mock data
    useEffect(() => {
        const mockDocuments: ApprovalDocument[] = [
            {
                id: 1,
                document_type: 'expense',
                title: '현장 안전장비 구매 지출결의서',
                requester: '김철수',
                requester_department: '건설사업부',
                amount: 1500000,
                request_date: '2024-12-20T09:30:00',
                status: 'pending',
                management_support_confirmed: false,
                description: '현장 작업자 안전장비(안전모, 안전화 등) 구매를 위한 지출결의서입니다.',
                priority: 'high'
            },
            {
                id: 2,
                document_type: 'travel',
                title: '현장 출장비 정산서',
                requester: '이영희',
                requester_department: '품질관리부',
                amount: 350000,
                request_date: '2024-12-19T14:15:00',
                status: 'first_approved',
                first_approver: '박관리',
                first_approval_date: '2024-12-19T16:30:00',
                management_support_confirmed: true,
                description: '부산 현장 품질점검 출장비 정산',
                priority: 'normal'
            },
            {
                id: 3,
                document_type: 'purchase',
                title: '사무용품 구매 요청서',
                requester: '최수진',
                requester_department: '관리부',
                amount: 280000,
                request_date: '2024-12-18T11:20:00',
                status: 'final_approved',
                first_approver: '박관리',
                final_approver: '김이사',
                first_approval_date: '2024-12-18T15:00:00',
                final_approval_date: '2024-12-19T09:00:00',
                management_support_confirmed: true,
                description: '프린터 토너 및 A4용지 구매',
                priority: 'low'
            },
            {
                id: 4,
                document_type: 'expense',
                title: '장비 수리비 지출결의서',
                requester: '정대호',
                requester_department: '안전관리부',
                amount: 850000,
                request_date: '2024-12-17T08:45:00',
                status: 'rejected',
                first_approver: '박관리',
                first_approval_date: '2024-12-17T16:00:00',
                management_support_confirmed: false,
                description: '크레인 정기 점검 및 수리',
                rejection_reason: '견적서 추가 제출 필요',
                priority: 'high'
            },
            {
                id: 5,
                document_type: 'other',
                title: '교육비 지원 신청서',
                requester: '홍길동',
                requester_department: '기술개발부',
                amount: 500000,
                request_date: '2024-12-16T13:30:00',
                status: 'returned',
                management_support_confirmed: false,
                description: '건축기술 관련 외부 교육 참석',
                rejection_reason: '교육 내용 상세 설명 보완 필요',
                priority: 'normal'
            }
        ];
        setDocuments(mockDocuments);
        setFilteredDocuments(mockDocuments);
        setLoading(false);
    }, []);

    // 검색 및 필터링
    useEffect(() => {
        let filtered = documents;

        if (searchTerm) {
            filtered = filtered.filter(doc => 
                doc.title.includes(searchTerm) || 
                doc.requester.includes(searchTerm) ||
                doc.requester_department.includes(searchTerm)
            );
        }

        if (selectedType !== 'all') {
            filtered = filtered.filter(doc => doc.document_type === selectedType);
        }

        if (selectedStatus !== 'all') {
            filtered = filtered.filter(doc => doc.status === selectedStatus);
        }

        if (selectedPriority !== 'all') {
            filtered = filtered.filter(doc => doc.priority === selectedPriority);
        }

        // 최신순 정렬
        filtered.sort((a, b) => new Date(b.request_date).getTime() - new Date(a.request_date).getTime());

        setFilteredDocuments(filtered);
    }, [documents, searchTerm, selectedType, selectedStatus, selectedPriority]);

    const getDocumentTypeLabel = (type: string) => {
        switch (type) {
            case 'expense': return '지출결의서';
            case 'purchase': return '구매요청서';
            case 'travel': return '출장비정산서';
            case 'other': return '기타';
            default: return type;
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'pending': return '대기중';
            case 'first_approved': return '1차승인';
            case 'final_approved': return '최종승인';
            case 'rejected': return '반려';
            case 'returned': return '보완요청';
            default: return status;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'pending': return 'warning';
            case 'first_approved': return 'info';
            case 'final_approved': return 'success';
            case 'rejected': return 'danger';
            case 'returned': return 'secondary';
            default: return 'default';
        }
    };

    const getPriorityLabel = (priority: string) => {
        switch (priority) {
            case 'high': return '긴급';
            case 'normal': return '보통';
            case 'low': return '낮음';
            default: return priority;
        }
    };

    const columns: Column<ApprovalDocument>[] = [
        {
            key: 'priority',
            header: '우선순위',
            width: '80px',
            align: 'center',
            render: (value) => {
                const color = value === 'high' ? '#dc3545' : value === 'normal' ? '#ffc107' : '#6c757d';
                return (
                    <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor: color + '20',
                        color: color
                    }}>
                        {getPriorityLabel(value)}
                    </span>
                );
            }
        },
        {
            key: 'document_type',
            header: '문서유형',
            width: '100px',
            align: 'center',
            render: (value) => getDocumentTypeLabel(value)
        },
        {
            key: 'title',
            header: '제목',
            sortable: true
        },
        {
            key: 'requester',
            header: '요청자',
            width: '100px'
        },
        {
            key: 'requester_department',
            header: '부서',
            width: '120px'
        },
        {
            key: 'amount',
            header: '금액',
            width: '120px',
            align: 'right',
            render: (value) => value ? `${value.toLocaleString()}원` : '-'
        },
        {
            key: 'request_date',
            header: '요청일',
            width: '150px',
            sortable: true,
            render: (value) => formatDate(value)
        },
        {
            key: 'status',
            header: '상태',
            width: '100px',
            align: 'center',
            render: (value) => {
                const statusInfo = getStatusInfo(value);
                return (
                    <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor: statusInfo.color + '20',
                        color: statusInfo.color
                    }}>
                        {statusInfo.label}
                    </span>
                );
            }
        },
        {
            key: 'management_support_confirmed',
            header: '경영지원',
            width: '80px',
            align: 'center',
            render: (value) => (
                <span style={{ color: value ? '#28a745' : '#dc3545' }}>
                    {value ? '✓' : '✗'}
                </span>
            )
        }
    ];

    const getStatusInfo = (status: string) => {
        switch (status) {
            case 'pending': return { label: '대기중', color: '#6c757d' };
            case 'first_approved': return { label: '1차승인', color: '#17a2b8' };
            case 'final_approved': return { label: '최종승인', color: '#28a745' };
            case 'rejected': return { label: '반려', color: '#dc3545' };
            case 'returned': return { label: '회수', color: '#ffc107' };
            default: return { label: status, color: '#6c757d' };
        }
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'high': return 'danger';
            case 'normal': return 'info';
            case 'low': return 'secondary';
            default: return 'default';
        }
    };

    const handleCreate = () => {
        setApprovalForm({
            document_type: 'expense',
            title: '',
            amount: 0,
            description: '',
            priority: 'normal'
        });
        setShowCreateModal(true);
    };

    const handleSaveDocument = () => {
        const newDocument: ApprovalDocument = {
            id: Math.max(...documents.map(d => d.id)) + 1,
            ...approvalForm,
            document_type: approvalForm.document_type as ApprovalDocument['document_type'],
            requester: '현재 사용자', // 실제로는 로그인한 사용자 정보
            requester_department: '현재 부서',
            request_date: new Date().toISOString(),
            status: 'pending',
            management_support_confirmed: false,
            priority: approvalForm.priority as ApprovalDocument['priority']
        };
        
        setDocuments([newDocument, ...documents]);
        setShowCreateModal(false);
        alert('결재 문서가 등록되었습니다.');
    };

    const handleApproval = (document: ApprovalDocument) => {
        setSelectedDocument(document);
        setShowApprovalModal(true);
    };

    const handleApprovalAction = (action: 'approve' | 'reject' | 'return', reason?: string) => {
        if (!selectedDocument) return;

        const updatedDocuments = documents.map(doc => {
            if (doc.id === selectedDocument.id) {
                const currentUser = userRole === 'admin' ? '김이사' : '박관리';
                const now = new Date().toISOString();

                if (action === 'approve') {
                    if (doc.status === 'pending') {
                        return {
                            ...doc,
                            status: 'first_approved' as ApprovalDocument['status'],
                            first_approver: currentUser,
                            first_approval_date: now,
                            management_support_confirmed: true
                        };
                    } else if (doc.status === 'first_approved' && userRole === 'admin') {
                        return {
                            ...doc,
                            status: 'final_approved' as ApprovalDocument['status'],
                            final_approver: currentUser,
                            final_approval_date: now
                        };
                    }
                } else if (action === 'reject') {
                    return {
                        ...doc,
                        status: 'rejected' as ApprovalDocument['status'],
                        rejection_reason: reason
                    };
                } else if (action === 'return') {
                    return {
                        ...doc,
                        status: 'returned' as ApprovalDocument['status'],
                        rejection_reason: reason
                    };
                }
            }
            return doc;
        });

        setDocuments(updatedDocuments);
        setShowApprovalModal(false);
        setSelectedDocument(null);
        alert(`결재가 ${action === 'approve' ? '승인' : action === 'reject' ? '반려' : '보완요청'} 처리되었습니다.`);
    };

    const handleDelete = (documentId: number) => {
        if (window.confirm('이 결재 문서를 삭제하시겠습니까?')) {
            setDocuments(documents.filter(doc => doc.id !== documentId));
        }
    };

    const getStatistics = () => {
        const total = documents.length;
        const pending = documents.filter(d => d.status === 'pending').length;
        const approved = documents.filter(d => d.status === 'final_approved').length;
        const needsAttention = documents.filter(d => 
            d.status === 'pending' || d.status === 'returned'
        ).length;
        const totalAmount = documents
            .filter(d => d.status === 'final_approved' && d.amount)
            .reduce((sum, d) => sum + (d.amount || 0), 0);
        
        return { total, pending, approved, needsAttention, totalAmount };
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('ko-KR').format(amount);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('ko-KR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const stats = getStatistics();

    if (loading) {
        return (
            <PageContainer>
                <Loading text="결재 정보를 불러오는 중..." />
            </PageContainer>
        );
    }

    return (
        <PageContainer className="approval-page">
            <PageHeader
                title="결재 관리"
                subtitle="지출결의서 및 각종 결재 문서 관리"
                actions={
                    <>
                        <Button variant="secondary" icon={<ArrowDownTrayIcon />}>
                            결재현황 다운로드
                        </Button>
                        <Button variant="primary" icon={<PlusIcon />} onClick={handleCreate}>
                            결재 신청
                        </Button>
                    </>
                }
            />

            <StatsGrid>
                <StatCard
                    title="총 결재건수"
                    value={`${stats.total}건`}
                    icon={
                        <svg fill="currentColor" viewBox="0 0 20 20">
                            <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z"/>
                            <path fillRule="evenodd" d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd"/>
                        </svg>
                    }
                    iconColor="total"
                />
                <StatCard
                    title="대기중"
                    value={`${stats.pending}건`}
                    icon={
                        <svg fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd"/>
                        </svg>
                    }
                    iconColor="warning"
                    valueColor="warning"
                />
                <StatCard
                    title="승인완료"
                    value={`${stats.approved}건`}
                    icon={
                        <svg fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                        </svg>
                    }
                    iconColor="success"
                    valueColor="success"
                />
                <StatCard
                    title="승인금액"
                    value={`${formatCurrency(stats.totalAmount)}원`}
                    icon={
                        <svg fill="currentColor" viewBox="0 0 20 20">
                            <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/>
                        </svg>
                    }
                    iconColor="primary"
                    valueColor="primary"
                />
            </StatsGrid>

            <FilterSection>
                <SearchInput
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="문서 제목, 신청자, 부서로 검색..."
                />
                <select 
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="form-select"
                >
                    <option value="all">전체 유형</option>
                    <option value="expense">지출결의서</option>
                    <option value="purchase">구매요청서</option>
                    <option value="travel">출장비정산서</option>
                    <option value="other">기타</option>
                </select>
                <select 
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="form-select"
                >
                    <option value="all">전체 상태</option>
                    <option value="pending">대기중</option>
                    <option value="first_approved">1차승인</option>
                    <option value="final_approved">최종승인</option>
                    <option value="rejected">반려</option>
                    <option value="returned">보완요청</option>
                </select>
                <select 
                    value={selectedPriority}
                    onChange={(e) => setSelectedPriority(e.target.value)}
                    className="form-select"
                >
                    <option value="all">전체 우선순위</option>
                    <option value="high">긴급</option>
                    <option value="normal">보통</option>
                    <option value="low">낮음</option>
                </select>
            </FilterSection>

            <TableContainer
                title="결재 문서 목록 (최신순)"
                resultCount={filteredDocuments.length}
            >
                <Table
                    columns={columns}
                    data={filteredDocuments}
                    pagination
                    pageSize={10}
                    defaultSortKey="request_date"
                    defaultSortOrder="desc"
                    stickyHeader
                    maxHeight="600px"
                    rowClassName={(row) => row.status === 'pending' ? 'pending-row' : ''}
                    actions={(doc) => (
                        <div className="action-buttons">
                            {(doc.status === 'pending' || doc.status === 'first_approved') && (
                                <button 
                                    className="btn btn-sm btn-approve"
                                    onClick={() => handleApproval(doc)}
                                >
                                    결재
                                </button>
                            )}
                            <button 
                                className="btn btn-sm btn-view"
                                onClick={() => alert('상세보기 기능 구현 예정')}
                            >
                                상세
                            </button>
                            {doc.status === 'pending' && (
                                <button 
                                    className="btn btn-sm btn-delete"
                                    onClick={() => handleDelete(doc.id)}
                                >
                                    삭제
                                </button>
                            )}
                        </div>
                    )}
                />
            </TableContainer>

            {/* 결재 신청 모달 */}
            {showCreateModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3>결재 신청</h3>
                            <button 
                                className="modal-close"
                                onClick={() => setShowCreateModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-row">
                                <div className="form-group">
                                    <label>문서 유형</label>
                                    <select
                                        value={approvalForm.document_type}
                                        onChange={(e) => setApprovalForm({...approvalForm, document_type: e.target.value})}
                                    >
                                        <option value="expense">지출결의서</option>
                                        <option value="purchase">구매요청서</option>
                                        <option value="travel">출장비정산서</option>
                                        <option value="other">기타</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>우선순위</label>
                                    <select
                                        value={approvalForm.priority}
                                        onChange={(e) => setApprovalForm({...approvalForm, priority: e.target.value})}
                                    >
                                        <option value="low">낮음</option>
                                        <option value="normal">보통</option>
                                        <option value="high">긴급</option>
                                    </select>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>제목</label>
                                <input
                                    type="text"
                                    value={approvalForm.title}
                                    onChange={(e) => setApprovalForm({...approvalForm, title: e.target.value})}
                                    placeholder="결재 문서 제목을 입력하세요"
                                />
                            </div>
                            <div className="form-group">
                                <label>금액 (원)</label>
                                <input
                                    type="number"
                                    value={approvalForm.amount}
                                    onChange={(e) => setApprovalForm({...approvalForm, amount: Number(e.target.value)})}
                                    placeholder="금액을 입력하세요 (해당 없으면 0)"
                                />
                            </div>
                            <div className="form-group">
                                <label>내용</label>
                                <textarea
                                    value={approvalForm.description}
                                    onChange={(e) => setApprovalForm({...approvalForm, description: e.target.value})}
                                    placeholder="결재 요청 내용을 상세히 입력하세요"
                                    rows={5}
                                />
                            </div>
                            <div className="approval-info">
                                <h4>결재 경로</h4>
                                <div className="approval-flow">
                                    <div className="flow-step">
                                        <span className="step-number">1</span>
                                        <span className="step-title">
                                            {userRole === 'admin' ? '경영지원이사' : '책임감리원'} (1차 결재)
                                        </span>
                                    </div>
                                    <div className="flow-arrow">→</div>
                                    <div className="flow-step">
                                        <span className="step-number">2</span>
                                        <span className="step-title">경영지원부 참조</span>
                                    </div>
                                    {userRole === 'user' && (
                                        <>
                                            <div className="flow-arrow">→</div>
                                            <div className="flow-step">
                                                <span className="step-number">3</span>
                                                <span className="step-title">경영지원이사 (최종 결재)</span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="btn btn-secondary"
                                onClick={() => setShowCreateModal(false)}
                            >
                                취소
                            </button>
                            <button 
                                className="btn btn-primary"
                                onClick={handleSaveDocument}
                                disabled={!approvalForm.title || !approvalForm.description}
                            >
                                신청
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 결재 처리 모달 */}
            {showApprovalModal && selectedDocument && (
                <div className="modal-overlay">
                    <div className="modal-content approval-modal">
                        <div className="modal-header">
                            <h3>결재 처리</h3>
                            <button 
                                className="modal-close"
                                onClick={() => setShowApprovalModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="document-info">
                                <h4>{selectedDocument.title}</h4>
                                <div className="info-grid">
                                    <div className="info-item">
                                        <span className="label">신청자:</span>
                                        <span className="value">{selectedDocument.requester}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="label">부서:</span>
                                        <span className="value">{selectedDocument.requester_department}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="label">금액:</span>
                                        <span className="value">
                                            {selectedDocument.amount ? `${formatCurrency(selectedDocument.amount)}원` : '-'}
                                        </span>
                                    </div>
                                    <div className="info-item">
                                        <span className="label">신청일:</span>
                                        <span className="value">{formatDate(selectedDocument.request_date)}</span>
                                    </div>
                                </div>
                                <div className="description">
                                    <span className="label">내용:</span>
                                    <p>{selectedDocument.description}</p>
                                </div>
                            </div>
                            
                            <div className="approval-actions">
                                <button 
                                    className="btn btn-success"
                                    onClick={() => handleApprovalAction('approve')}
                                >
                                    승인
                                </button>
                                <button 
                                    className="btn btn-warning"
                                    onClick={() => {
                                        const reason = prompt('보완 요청 사유를 입력하세요:');
                                        if (reason) handleApprovalAction('return', reason);
                                    }}
                                >
                                    보완요청
                                </button>
                                <button 
                                    className="btn btn-danger"
                                    onClick={() => {
                                        const reason = prompt('반려 사유를 입력하세요:');
                                        if (reason) handleApprovalAction('reject', reason);
                                    }}
                                >
                                    반려
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </PageContainer>
    );
};

export default ApprovalManagementPage;
